// src/components/MobileBottomNav.jsx
//
// Fixed bottom-of-viewport quick-action bar for phones. Hidden on md+
// (Layout keeps rendering the drawer there). Exposes up to 5 primary
// destinations: Dashboard, Alarms, Tickets, Locations, Notifications.
// Permission-gated (admin-only Alarms, permission-gated Tickets) —
// items the user cannot access are simply omitted.
//
// Behavior:
//   - Active route highlighted via `location.pathname.startsWith(path)`.
//   - Notification slot shows an unread badge (uses notificationService).
//   - Respects iOS safe-area-inset-bottom (home indicator).
//   - Height is reported via the `BOTTOM_NAV_HEIGHT` export so Layout can
//     pad the main content and the notifications drawer can clear it.

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  LocalHospital as AlarmIcon,
  BugReport as TicketsIcon,
  LocationOn as LocationsIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import notificationService from '../services/notificationService';
import useVisibilityPoll from '../hooks/useVisibilityPoll';

export const BOTTOM_NAV_HEIGHT = 56;

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { hasPermission } = usePermissions();
  const [unread, setUnread] = useState(0);

  const load = React.useCallback(async () => {
    if (!isAuthenticated || !user) return;
    try {
      // notificationService.getUnreadCount() resolves to a plain number
      // (see NotificationCenter usage). Guard against future shape drift.
      const res = await notificationService.getUnreadCount();
      const n = typeof res === 'number' ? res : (res?.count ?? res?.unreadCount ?? 0);
      setUnread(n);
    } catch {
      /* ignore — bell in the app bar handles its own errors */
    }
  }, [isAuthenticated, user]);

  // Poll every 30s while authenticated and the tab is visible.
  useVisibilityPoll(load, 30000, !!(isAuthenticated && user));

  const isAdmin = user?.role === 'admin';
  const canSeeTickets = hasPermission ? hasPermission('tickets.view') || true : true;

  // Build the list, filter invisibles. Keeps 3-5 slots ideally.
  const items = [
    { value: '/dashboard', label: 'Home', icon: <DashboardIcon />, show: true },
    { value: '/alarms', label: 'Alarms', icon: <AlarmIcon />, show: isAdmin },
    { value: '/tickets', label: 'Tickets', icon: <TicketsIcon />, show: canSeeTickets },
    { value: '/locations', label: 'Locations', icon: <LocationsIcon />, show: true },
    {
      value: '/notifications',
      label: 'Alerts',
      icon: (
        <Badge badgeContent={unread} color="error" max={99} overlap="circular">
          <NotificationsIcon />
        </Badge>
      ),
      show: true,
    },
  ].filter((x) => x.show);

  const matchedValue = items.find((it) => location.pathname.startsWith(it.value))?.value || false;

  return (
    <Paper
      elevation={6}
      square
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        display: { xs: 'block', md: 'none' },
        // iOS safe area — pad the bar itself so the actions don't sit
        // on top of the home indicator.
        pb: 'env(safe-area-inset-bottom, 0px)',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <BottomNavigation
        showLabels
        value={matchedValue}
        onChange={(_e, v) => {
          if (v && v !== matchedValue) navigate(v);
        }}
        sx={{
          height: BOTTOM_NAV_HEIGHT,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 56,
            padding: '6px 8px 8px',
            transition: 'color 180ms ease, transform 180ms ease',
          },
          '& .MuiBottomNavigationAction-root.Mui-selected': {
            // Subtle scale-up on selection gives a native-app tap feel.
            transform: 'translateY(-1px) scale(1.03)',
          },
          '& .MuiBottomNavigationAction-root .MuiSvgIcon-root': {
            transition: 'transform 180ms ease',
          },
          '& .MuiBottomNavigationAction-root.Mui-selected .MuiSvgIcon-root': {
            transform: 'scale(1.06)',
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.72rem',
            transition: 'font-size 180ms ease, opacity 180ms ease',
            '&.Mui-selected': { fontSize: '0.75rem', fontWeight: 600 },
          },
        }}
      >
        {items.map((it) => (
          <BottomNavigationAction
            key={it.value}
            value={it.value}
            label={it.label}
            icon={it.icon}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;
